import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Snowflake, HardHat, MapPin, Boxes, LandPlot, ArrowRight, ShieldCheck, Phone, Mail } from "lucide-react";
import api from "@/lib/api";
import { Section } from "@/components/common";
import PowerStudyBanner from "@/components/PowerStudyBanner";

const AUDIENCE = [
  [Zap, "Power & energy", "Generation, transmission, substations, backup, and grid-interconnection services."],
  [Snowflake, "Cooling & thermal", "Air, evaporative, liquid, immersion, and heat-reuse systems and service providers."],
  [Boxes, "Equipment & systems", "Servers, racks, switchgear, UPS, controls, and critical infrastructure vendors."],
  [HardHat, "Construction & MEP", "GCs, electrical, mechanical, commissioning, and specialty contractors."],
  [MapPin, "Site & development", "Site selection, civil, permitting, and development advisory services."],
  [LandPlot, "Land services", "Land acquisition, brokerage, surveying, and environmental services."],
];

export default function Advertise() {
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setBiz(r.data.business)); }, []);

  return (
    <div>
      {/* Hero */}
      <div className="grid-bg border-b border-white/10">
        <Section className="py-16 sm:py-24">
          <p className="eyebrow mb-3">Advertise &amp; Launch Partners</p>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.05]">
            Reach decision-makers across the <span className="text-[#84CC16]">data-center buildout.</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed">
            DYORUSA is an independent research platform read by residents, operators, developers, utilities, and public officials.
            Put your company in front of that audience with clearly labeled, transparent placements.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button data-testid="hero-view-pricing" onClick={() => navigate("/advertise/pricing")}
              className="inline-flex items-center gap-2 bg-[#84CC16] hover:bg-[#9ee325] text-[#0B0F17] font-bold px-6 py-3.5 rounded-full transition-colors glow-lime">
              View pricing &amp; packages <ArrowRight size={18} />
            </button>
            <button data-testid="hero-request" onClick={() => navigate("/advertise/signup")}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-blue-500/60 text-white font-semibold px-6 py-3.5 rounded-full transition-colors">
              Request a placement
            </button>
          </div>
        </Section>
      </div>

      <PowerStudyBanner testid="advertise-power-study-banner" />

      {/* Who it's for */}
      <Section className="py-14 sm:py-20">
        <p className="eyebrow mb-2">Built for companies serving data centers</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-8">Relevant to your corner of the industry</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map(([Icon, title, desc]) => (
            <div key={title} data-testid={`audience-${title}`} className="card p-6">
              <Icon className="text-[#3B82F6] mb-3" size={22} />
              <h3 className="font-display font-semibold text-lg text-white">{title}</h3>
              <p className="text-slate-400 text-sm mt-1.5">{desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Transparency */}
      <Section className="pb-14">
        <div className="rounded-xl border border-[#84CC16]/20 bg-[#84CC16]/5 px-6 py-5 flex items-start gap-3">
          <ShieldCheck size={20} className="text-[#84CC16] mt-0.5 shrink-0" />
          <div>
            <p className="text-white font-semibold">Straight talk on what advertising does — and doesn't — do</p>
            <p className="text-lime-100/85 text-sm mt-1 leading-relaxed">
              Every paid placement is clearly labeled "Advertisement" or "Sponsored." Payment never changes our research, source
              selection, project counts, rankings, or editorial conclusions. We don't guarantee audience size, traffic, reach, or
              results — we report the real performance we can verify.
            </p>
          </div>
        </div>
      </Section>

      {/* CTA + contact */}
      <Section className="pb-20">
        <div className="card p-8 sm:p-10 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">See pricing and reserve your placement</h2>
            <p className="text-slate-400 mt-3">Fixed prices, clear terms, and a simple request form. No payment is collected until a provider is configured and you approve.</p>
            <button data-testid="cta-view-pricing" onClick={() => navigate("/advertise/pricing")}
              className="mt-5 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold px-6 py-3 rounded-full transition-colors">
              View pricing <ArrowRight size={16} />
            </button>
          </div>
          {biz && (
            <div className="border-t lg:border-t-0 lg:border-l border-white/10 lg:pl-8 pt-6 lg:pt-0" data-testid="business-identity">
              <p className="eyebrow mb-3">Contact</p>
              <p className="text-white font-semibold">{biz.principal}</p>
              <p className="text-slate-300">{biz.name}</p>
              <div className="flex flex-col gap-1.5 mt-3 text-sm">
                <a href={`tel:+19127238577`} className="inline-flex items-center gap-2 text-[#3B82F6] hover:text-[#84CC16]"><Phone size={14} /> {biz.phone}</a>
                <a href={`mailto:${biz.email}`} className="inline-flex items-center gap-2 text-[#3B82F6] hover:text-[#84CC16]"><Mail size={14} /> {biz.email}</a>
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
